import type { DocPage } from '../types'
import { media } from '../media'

export const formats: DocPage = {
  id: 'formats',
  title: 'Formats overview',
  description:
    'Matrix of supported Source and Target formats, how Select Format works, and pointers to per-format chapters.',
  category: 'Formats',
  sections: [
    { id: 'select', title: 'How Select Format works' },
    { id: 'matrix', title: 'Format matrix' },
    { id: 'entitlements', title: 'Entitlements' },
    { id: 'chapters', title: 'Per-format chapters' },
    { id: 'configs', title: 'Saved configurations' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'select',
      text: 'How Select Format works',
    },
    {
      type: 'steps',
      items: [
        'On Source or Target, open Select Format.',
        'Pick a format from the catalog (locked items need entitlements).',
        'Choose New to start a fresh connection, or load a saved Model Configuration.',
        'If entitled, use Create Custom Config after a successful setup to store settings.',
        'Complete the format form (connection, files, model mode, styles, filters).',
        'Submit so the model tree (or scratch destination) is ready for mapping/transfer.',
      ],
    },
    {
      type: 'figure',
      src: media.formatDropdown,
      alt: 'Select Format dropdown',
      caption:
        'Figure: Select Format menu with the full format catalog and Create Custom Config.',
      size: 'compact',
    },
    {
      type: 'paragraph',
      text: 'Source and Target are gated separately. A format may be available as source, target, both, or (for MGDM Pipeline) primarily as a target/API-style entry in the catalog.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'matrix',
      text: 'Format matrix',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Format', 'Source', 'Target', 'Connection / notes'],
      rows: [
        ['PostGIS', 'Yes', 'Yes', 'Host, port, user, password, database multi-select; scratch catalog; style export'],
        ['Smallworld', 'Yes', 'No', 'Host, databases, ACE; style import / display style'],
        ['AutoCAD', 'Yes', 'Yes', '.dwg / .dxf; Translator/Layer/Entity; style import; EPSG'],
        ['Geopackage', 'Yes', 'Yes', '.gpkg; Override Existing; style export'],
        ['OGC API Features', 'Yes', 'No', 'Service root URL with /collections'],
        ['GeoParquet', 'Yes', 'Yes', '.parquet file'],
        ['MGDM Pipeline', 'API', 'Yes', 'Standard / Extended; Interlis .xtf'],
        ['GeoJSON', 'Yes', 'Yes', 'Files / folder / zip; subfolders'],
        ['Shapefile', 'Yes', 'Yes', '.shp / folder / zip'],
        ['KML', 'Yes', 'Yes', '.kml / .kmz; optional style import'],
        ['Text/CSV/Excel', 'Yes', 'Yes', 'General / Text/CSV / Excel / Geometry tabs'],
        ['X-Format', 'Yes', 'Yes', 'Anchor .xf / .tm; write .xf and/or .tm; pretty JSON'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'entitlements',
      text: 'Entitlements',
    },
    {
      type: 'paragraph',
      text: 'Each format appears as keys such as postgis-source, cad-target, and so on. Items you are not entitled to appear disabled with a lock or crown. Only LIVE subscriptions unlock formats. See Account & subscription.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'chapters',
      text: 'Per-format chapters',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'PostGIS',
          definition: 'Connection fields, DB multi-select, scratch catalog, style export accordion.',
        },
        {
          term: 'Smallworld',
          definition: 'Host, ACE, display style / style group, Import Smallworld styles.',
        },
        {
          term: 'AutoCAD',
          definition: 'Model modes, entity tables, explode/retain, fidelity, EPSG, mapped vs scratch write.',
        },
        {
          term: 'File formats',
          definition:
            'GeoPackage, GeoJSON, Shapefile, KML, GeoParquet, OGC API Features, MGDM/Interlis, X-Format.',
        },
        {
          term: 'Text / CSV / Excel',
          definition: 'All tabs field-by-field plus scratch output options.',
        },
        {
          term: 'Styles import & export',
          definition: 'CAD/KML import options and QML/SLD/XStyles/layer_styles export tables.',
        },
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'configs',
      text: 'Saved configurations',
    },
    {
      type: 'paragraph',
      text: 'Reusable connection packages are covered in depth under Model configurations (New, Save, Save as new, Default, Delete, UNSAVED, Create Custom Config).',
    },
  ],
}
